/**
 * **Connections** — one screen. `APPLICATION_SPEC.md` §5.3, SOW workstream 7.
 *
 * Rewritten 9 Sep. The first version split this into a *Pipelines* tab and a
 * *Coverage* tab, and that was the mistake: **"is the data fresh?" needs the run
 * state and the coverage together**, so a reader had to look in two places and
 * map between them by name. The three strips sat in one card and the three runs
 * in another.
 *
 * Now **the source is the unit**. One card per pipeline, carrying its own state,
 * its own freshness, its own strip and its own action — so nothing has to be
 * cross-referenced, and `Run now` sits beside the thing it runs.
 *
 * Three decisions worth knowing before editing:
 *
 * 1. **The second line of each card is source-specific.** Snapchat says *data
 *    through*, ClickUp says *matched 290 of 349*, thumbnails says *1,122
 *    mirrored*. Same slot, different substance, because the sources are not
 *    interchangeable and a generic template would flatten that.
 * 2. **`Run now` promises queued, not started.** The web container has
 *    SELECT-only on `snap.*` and physically cannot run a pipeline, so the button
 *    writes a request the worker claims within ~30s. The states say so.
 * 3. **The strip is the one bold thing.** It is the only object here a table
 *    could not replace, and it carries the 18 October deadline. Everything
 *    around it stays quiet.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { ChannelCoverageGrid } from '../components/ChannelCoverageGrid'
import { formatDate, formatPipelineClock, formatPipelineMoment } from '../lib/dates'
import { CoverageLegend, CoverageStrip } from '../components/CoverageStrip'
import { headCell, numericHeadCell } from '../components/DataTable'
import { Hint } from '../components/Hint'
import { InfoDot } from '../components/InfoDot'
import { SOURCE_MARKS } from '../components/SourceMarks'
import { StatusDot, type StatusState } from '../components/StatusDot'
import { Button, Card, Note, Skeleton, Tag } from '../components/ui'
import {
  ApiError,
  api,
  type CoverageResponse,
  type PipelineHealth,
  type RunRow,
} from '../lib/api'
import { ViewHeader } from '../shell/ViewHeader'
import { useSession } from '../lib/session'
import { formatNumber } from '../lib/numbers'

/** Run state → the status palette. Saturated green and red belong to status,
 *  which is exactly what this is. */
const RUN_STATE: Record<PipelineHealth['state'], { tone: StatusState; label: string }> = {
  succeeded: { tone: 'good', label: 'Succeeded' },
  running: { tone: 'idle', label: 'Running Now' },
  partial: { tone: 'warning', label: 'Partial' },
  failed: { tone: 'critical', label: 'Failed' },
  // Derived from the age of an unfinished run, never stored.
  abandoned: { tone: 'serious', label: 'Abandoned' },
  reverted: { tone: 'idle', label: 'Reverted' },
  never_run: { tone: 'idle', label: 'Never Run' },
}

const SOURCE_NAMES: Record<string, string> = {
  snap: 'Snapchat',
  clickup: 'ClickUp',
  thumbnails: 'Thumbnails',
}

/** What each source pulls, in the client's terms rather than ours. */
const SOURCE_SUBTITLES: Record<string, string> = {
  snap: 'Every Metric, for 13 Channels',
  clickup: 'Episode Number, Author and Re-Run Flag',
  thumbnails: 'Story Artwork, Saved Before Snap’s Links Expire',
}

function ago(iso: string | null): string {
  if (!iso) return 'never'
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

function until(iso: string | null): string {
  if (!iso) return '—'
  const minutes = Math.round((new Date(iso).getTime() - Date.now()) / 60000)
  if (minutes < 60) return `in ${Math.max(minutes, 1)}m`
  const hours = Math.round(minutes / 60)
  return hours < 48 ? `in ${hours}h` : `in ${Math.round(hours / 24)}d`
}

/** `MM/DD/YYYY`, from the one formatter — see `lib/dates.ts`. */
const day = formatDate

/** A run timestamp keeps its clock: "did last night's run happen" is not
 *  answerable to the day.
 *
 *  **Pacific first, UTC in brackets — CN-7.** The reader is a person in
 *  California asking whether tonight's run has gone yet; the schedule is
 *  configured in UTC. Both are true and the screen says both. See
 *  `formatPipelineMoment` for why the zone abbreviation is emitted rather than
 *  written — it is PDT, not PST, until 1 November.
 *
 *  🚫 **This is the only place in the app that leaves UTC.** Every *date* —
 *  stat days, coverage cells, table cells, exports — stays UTC via
 *  `lib/dates`, because Snap's day is UTC and rendering it locally would shift
 *  a channel's Monday into its Sunday for anyone west of Greenwich. */
function stamp(iso: string | null): string {
  return formatPipelineMoment(iso)
}

function duration(seconds: number | null): string {
  if (seconds === null) return '—'
  return seconds < 90 ? `${seconds.toFixed(1)}s` : `${Math.round(seconds / 60)}m`
}

const numberStyle = { fontVariantNumeric: 'tabular-nums' } as const
/** How many missing units a day's drill-down lists before summarising. */
const DETAIL_LIMIT = 12

/**
 * After this long, a queued run is reported as overdue rather than pending.
 *
 * The worker polls every 30s, so a few multiples of that is comfortably past
 * "any moment now" without firing on a slow tick. Measured need: a request sat
 * pending for 19 minutes on a machine whose worker had never been started, and
 * the button said "Queued" throughout.
 */
const STALE_QUEUE_SECONDS = 150
const cell = { padding: '0.4rem 0.75rem', borderTop: '1px solid var(--border)' } as const

/**
 * The action. **It queues; it does not start.**
 *
 * Every state here says what is true rather than what would sound better:
 * `Queued` while the row waits, `Running` once the worker has it, and the
 * tooltip names the side effect — Donovan's convention, and the best thing in
 * that codebase.
 */
function RunNow({
  health,
  onRun,
  pending,
}: {
  health: PipelineHealth
  onRun: () => void
  pending: boolean
}) {
  const open = health.trigger_state
  if (open === 'claimed' || health.state === 'running') {
    return (
      <span className="text-xs" style={{ color: 'var(--text-label)' }}>
        <StatusDot state="idle">Running</StatusDot>
      </span>
    )
  }
  if (open === 'pending' || pending) {
    /**
     * 🔴 **A queue with nothing draining it must say so.**
     *
     * A request sat `pending` for 19 minutes on a machine where the worker was
     * never started, and this said a calm "Queued" the whole time. That is the
     * silent-failure class this whole page exists to prevent — the strip refuses
     * to let a missing day look like a quiet one, and then the button next to it
     * let a dead queue look like a busy one.
     *
     * So past a few multiples of the poll interval it stops reassuring and
     * starts reporting. The wording names the likely cause, because *"still
     * waiting"* is not something a reader can act on and *"the worker may not be
     * running"* is.
     */
    const queuedFor = health.trigger_requested_at
      ? (Date.now() - new Date(health.trigger_requested_at).getTime()) / 1000
      : 0
    const overdue = queuedFor > STALE_QUEUE_SECONDS

    return (
      <span className="text-xs" style={{ color: 'var(--text-label)' }}>
        <StatusDot state={overdue ? 'serious' : 'idle'}>
          {overdue ? `Queued ${Math.round(queuedFor / 60)}m ago` : 'Queued'}
        </StatusDot>
        <InfoDot
          note={
            overdue
              ? 'This Should Have Started by Now. Nothing Is Lost — It Will Run as Soon as the Scheduler Is Back.'
              : 'This Starts in Under a Minute.'
          }
        />
      </span>
    )
  }
  return (
    <Button variant="secondary" onClick={onRun}>
      Run Now
      <InfoDot note={`Fetch ${SOURCE_NAMES[health.pipeline] ?? health.pipeline} data now instead of waiting for tonight.`} />
    </Button>
  )
}

/** The line under the heading. Source-specific by design — see the module note. */
function Freshness({ health }: { health: PipelineHealth }) {
  const bits: React.ReactNode[] = []

  if (health.data_through) {
    bits.push(
      <span key="through">
        Data through <span className="is-num">{day(health.data_through)}</span>
        <InfoDot note="The Most Recent Day with Data." />
      </span>,
    )
  }
  // **The clock is visible, not only on hover — CN-7, corrected 16 Sep.**
  //
  // The relative span stays in front because it is what answers *"is this
  // stale?"* at a glance; the absolute Pacific time follows it because that is
  // the thing a person actually reads off this card, and a `title` is not
  // showing it. The hover keeps the UTC, which is what reconciles the screen
  // against `app/pipelines/schedule.py`.
  //
  // 🔴 **`Hint`, not `title` — Nilay, 16 Sep: *"it takes a long time for the
  // tooltip to show up, can we use tooltips we use elsewhere in the project?"***
  //
  // These were native `title` attributes, which is the exact thing `Hint` was
  // built to replace: the browser holds it ~1s before showing anything, renders
  // it in the OS font outside the token system, and gives no way to know it is
  // there. `Hint`'s own module note says as much — *"not fast enough to read a
  // table with"* — and every other tooltip in the app already uses it. This
  // screen was the last holdout, which is why the Pacific time read as missing
  // twice: it was there, behind a tooltip nobody waited for.
  const lastRun = health.last_success_at ?? health.last_attempt_at
  const attemptDiffers =
    health.last_attempt_at !== null && health.last_attempt_at !== health.last_success_at
  bits.push(
    <Hint
      key="ran"
      label={
        <>
          {/* **Named, because the visible figure and this one can differ.**
              The line outside counts from the last *success*; a pipeline that
              has failed since has a later *attempt*, and printing one bare
              timestamp under a relative span derived from the other is how a
              reader concludes the screen is lying. */}
          Last success {formatPipelineMoment(health.last_success_at)}
          {attemptDiffers && (
            <>
              <br />
              Last attempt {formatPipelineMoment(health.last_attempt_at)}
            </>
          )}
        </>
      }
    >
      <span>
        ran {ago(lastRun)} · <span className="is-num">{formatPipelineClock(lastRun)}</span>
      </span>
    </Hint>,
  )
  if (health.next_run_at && !health.trigger_state) {
    bits.push(
      <Hint
        key="next"
        // 🔴 **The schedule note is NOT in here.** It was, for about an hour,
        // and it read *"Daily 2:00 UTC with a 7 day re-pull"* — Nilay: *"its too
        // much detail"*. Right: the question this hover answers is *when does it
        // next run*, and the cadence plus the re-pull window is pipeline
        // configuration that belongs in `schedule.py`, not on a card.
        label={<>Next run {formatPipelineMoment(health.next_run_at)}</>}
        // **The clock is on the hover here, and visible on `ran` — CN-8,
        // 17 Sep.** Not an inconsistency: the two bits answer different
        // questions. *Did last night's run happen?* is a question about a
        // specific past instant, so the timestamp is the answer and hiding it
        // is what CN-7 had to undo. *When does it run next?* is answered by
        // `in 13h`; the wall-clock time is a detail, and printing it put two
        // numbers on the line where one is read. Nilay, 17 Sep: *"only show
        // next in 13h and on hover we show the next time."*
        //
        // 🔴 **The dotted underline is load-bearing, not decoration.** CN-7 was
        // exactly this — a Pacific time that existed only behind a hover, on a
        // span that gave no sign it could be hovered, and so read as missing
        // twice. Moving a fact onto a hover is only safe when the hover
        // advertises itself.
        style={{
          textDecoration: 'underline dotted',
          textDecorationColor: 'var(--border-strong)',
          textUnderlineOffset: '2px',
          cursor: 'help',
        }}
      >
        next {until(health.next_run_at)}
      </Hint>,
    )
  }
  if (health.rows_written !== null) {
    bits.push(
      <span key="rows" className="is-num">
        {formatNumber(health.rows_written)} rows
      </span>,
    )
  }
  // Deliberately NOT the completeness figure: the strip's own header carries
  // it two lines below, and saying it twice made the card read as padded.
  if (health.detail) {
    bits.push(
      <span key="detail" className="is-num">
        {health.detail}
      </span>,
    )
  }

  return (
    // 🔴 **A `div`, not a `p` — found by hovering, 17 Sep.** `HintBubble` renders
    // a `div` (and the coverage tooltip a `dl`), and a block element inside a
    // `<p>` is invalid nesting: React logs `validateDOMNesting` as a console
    // *error*, and a real HTML parser closes the paragraph early. It only fires
    // while a bubble is open, which is why a page that verifies clean on load
    // never showed it. Nothing here needs to be a paragraph.
    <div className="mt-1 mb-0 flex flex-wrap gap-x-3 text-xs" style={{ color: 'var(--text-label)' }}>
      {bits}
    </div>
  )
}

function SourceCard({
  health,
  coverage,
  runs,
  canTrigger,
  onRun,
  onRefetchDay,
  pendingPipeline,
}: {
  health: PipelineHealth
  coverage: CoverageResponse
  runs: RunRow[]
  canTrigger: boolean
  onRun: (pipeline: string) => void
  onRefetchDay: (pipeline: string, day: string) => void
  pendingPipeline: string | null
}) {
  const [openDay, setOpenDay] = useState<string | null>(null)
  // CO-2's grid is 13 rows deep, so it opens on request rather than by default.
  const [showGrid, setShowGrid] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const strip = coverage.strips.find((entry) => entry.pipeline === health.pipeline)
  const Mark = SOURCE_MARKS[health.pipeline]
  const state = RUN_STATE[health.state]
  const name = SOURCE_NAMES[health.pipeline] ?? health.pipeline
  const selected = strip?.days.find((entry) => entry.day === openDay)

  const detail = useQuery({
    queryKey: ['coverage-day', openDay],
    queryFn: () => api.coverageDay(openDay as string),
    enabled: openDay !== null && strip?.basis === 'coverage_ledger',
  })

  const dayRuns = openDay
    ? runs.filter(
        (run) => run.pipeline === health.pipeline && run.started_at.slice(0, 10) === openDay,
      )
    : runs.filter((run) => run.pipeline === health.pipeline)

  return (
    <Card>
      <div className="flex flex-wrap items-start gap-3">
        {Mark && (
          <span style={{ color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
            <Mark size={20} />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <h2 className="m-0 text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
              {name}
            </h2>
            <StatusDot state={state.tone}>
              <span className="text-xs">{state.label}</span>
            </StatusDot>
            {/* **No run-status tag here, deliberately (Nilay, 9 Sep).**
                It read "1 partial in 7 days" over an entirely green strip, and
                even once the wording was accurate it was still noise: the card
                already states the current state, and the strip states coverage
                — which is the question. A 7-day tally of run *statuses* adds a
                third number that looks like it disagrees with the other two,
                because a partial run whose days a later run filled is not a
                gap. The counts are still on the API and the runs themselves are
                one click away in the run history, so nothing is hidden. */}
          </div>
          <p className="mt-0.5 mb-0 text-xs" style={{ color: 'var(--text-secondary)' }}>
            {SOURCE_SUBTITLES[health.pipeline]}
          </p>
          <Freshness health={health} />
        </div>
        {canTrigger && health.triggerable && (
          <RunNow
            health={health}
            pending={pendingPipeline === health.pipeline}
            onRun={() => onRun(health.pipeline)}
          />
        )}
      </div>

      {health.last_error && (
        <p
          className="is-mono mt-3 mb-0 text-xs"
          style={{ color: 'var(--status-critical)', wordBreak: 'break-word' }}
        >
          {health.last_error}
        </p>
      )}

      {strip && (
        <div className="mt-4">
          <CoverageStrip
            strip={strip}
            showName={false}
            boundary={coverage.earliest_reachable}
            historyStart={coverage.history_start}
            selectedDay={openDay}
            onSelectDay={(value) => setOpenDay((current) => (current === value ? null : value))}
          />
        </div>
      )}

      {/* **CO-2 — the per-channel grid lands here, not on Channel Overview.**
          The strip above answers *which days are incomplete*; this answers
          *which channels made them so*. Behind a disclosure because it is 13
          rows deep and the card's job is a one-glance state.

          Snap only: ClickUp has no per-channel ingest ledger, and thumbnails is
          per-asset rather than per-channel-day. */}
      {health.pipeline === 'snap' && strip && strip.days.length > 0 && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setShowGrid((value) => !value)}
            aria-expanded={showGrid}
            className="text-xs"
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'var(--brand-ink)',
              cursor: 'pointer',
            }}
          >
            {showGrid ? 'Hide Per-Channel Detail' : 'Per-Channel Detail'}
          </button>
          {showGrid && (
            <div className="mt-2">
              <ChannelCoverageGrid
                from={strip.days[0]!.day}
                to={strip.days[strip.days.length - 1]!.day}
                channels={[]}
              />
            </div>
          )}
        </div>
      )}

      {openDay && (
        <div
          className="mt-3 px-3 py-2"
          style={{ background: 'var(--bg-page)', borderRadius: 'var(--radius-md)' }}
        >
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
              {day(openDay)}
            </span>
            <span className="text-xs" style={{ color: 'var(--text-label)' }}>
              {selected?.label}
            </span>
            {/* **The trigger that matters against the clock.** A day that failed
                inside the 90-day window is recoverable only until it is not, and
                until now recovering it meant a human with a shell. */}
            {canTrigger &&
              strip?.basis === 'coverage_ledger' &&
              selected &&
              selected.state !== 'complete' &&
              openDay >= coverage.earliest_reachable && (
                <Button variant="secondary" onClick={() => onRefetchDay(health.pipeline, openDay)}>
                  Re-Fetch This Day
                  <InfoDot note="Fetches This Day Again for All 13 Channels." />
                </Button>
              )}
            {selected &&
              selected.state !== 'complete' &&
              openDay < coverage.earliest_reachable && (
                <span className="text-xs" style={{ color: 'var(--status-critical)' }}>
                  Past Snap’s 90-day Boundary. This Day Can No Longer Be Fetched.
                </span>
              )}
            <button
              type="button"
              onClick={() => setOpenDay(null)}
              className="ml-auto text-xs"
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--brand-ink)',
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>

          {strip?.basis === 'coverage_ledger' && (
            <div className="mt-2">
              {detail.isLoading ? (
                <Skeleton rows={2} />
              ) : !detail.data?.length ? (
                <p className="m-0 text-xs" style={{ color: 'var(--text-label)' }}>
                  Every Expected Unit Landed on This Day.
                </p>
              ) : (
                <>
                  {/* **Summarised, then capped.** A day missing every channel
                      lists 52 units, which pushed the other two source cards
                      off the screen — and 52 rows of the same three words is
                      not more informative than the count plus a sample. */}
                  <p className="mt-0 mb-1 text-xs" style={{ color: 'var(--text-secondary)' }}>
                    {detail.data.length} of {selected?.expected ?? 0} Expected Units Missing,
                    Across{' '}
                    {new Set(detail.data.map((row) => row.profile_id)).size} Channels.
                  </p>
                  <ul
                    className="m-0 flex flex-col gap-0.5 text-xs"
                    style={{ listStyle: 'none', padding: 0 }}
                  >
                    {detail.data.slice(0, DETAIL_LIMIT).map((row) => (
                      <li key={`${row.profile_id}-${row.dataset}`}>
                        <span style={{ color: 'var(--text-primary)' }}>{row.display_label}</span>
                        <span className="is-mono ml-2" style={{ color: 'var(--text-label)' }}>
                          {row.dataset}
                        </span>
                        <span className="ml-2" style={{ color: 'var(--text-label)' }}>
                          {row.status === 'never_claimed' ? 'never claimed' : row.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {detail.data.length > DETAIL_LIMIT && (
                    <p className="mt-1 mb-0 text-xs" style={{ color: 'var(--text-label)' }}>
                      and {detail.data.length - DETAIL_LIMIT} More. Export the Ingest
                      Coverage Ledger from Raw Data for the Full List.
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          className="text-xs"
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--brand-ink)',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          {expanded ? 'Hide Run History' : `Run History · ${dayRuns.length} runs`}
          {openDay && !expanded ? ` on ${day(openDay)}` : ''}
        </button>

        {expanded && (
          <div className="mt-2" style={{ overflowX: 'auto' }}>
            {dayRuns.length === 0 ? (
              <p className="m-0 text-xs" style={{ color: 'var(--text-label)' }}>
                No Runs Recorded{openDay ? ` on ${day(openDay)}` : ''}.
              </p>
            ) : (
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <caption className="sr-only">{name} run history</caption>
                <thead>
                  <tr>
                    <th scope="col" style={headCell}>Started</th>
                    <th scope="col" style={headCell}>Trigger</th>
                    <th scope="col" style={headCell}>Who</th>
                    <th scope="col" style={headCell}>Status</th>
                    <th scope="col" style={numericHeadCell}>
                      Duration
                    </th>
                    <th scope="col" style={numericHeadCell}>
                      Rows
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {dayRuns.slice(0, 12).map((run) => (
                    <tr key={run.id}>
                      <td style={{ ...cell, ...numberStyle }}>
                        {stamp(run.started_at)}
                        {/* The clock answers "when", this answers "how long
                            ago" — which is the question somebody checking on a
                            pipeline actually has. */}
                        <span className="block text-xs" style={{ color: 'var(--text-label)' }}>
                          {ago(run.started_at)}
                        </span>
                      </td>
                      <td style={cell}>
                        <Tag>{run.trigger}</Tag>
                      </td>
                      <td style={cell}>
                        {run.requested_by ?? (
                          <span style={{ color: 'var(--text-label)' }}>—</span>
                        )}
                      </td>
                      <td style={cell}>
                        <StatusDot
                          state={RUN_STATE[run.status as PipelineHealth['state']]?.tone ?? 'idle'}
                        >
                          {run.status}
                        </StatusDot>
                      </td>
                      <td style={{ ...cell, ...numberStyle, textAlign: 'right' }}>
                        {duration(run.duration_seconds)}
                      </td>
                      <td style={{ ...cell, ...numberStyle, textAlign: 'right' }}>
                        {run.rows_written === null
                          ? '—'
                          : formatNumber(run.rows_written)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </Card>
  )
}

export function Connections() {
  const { me } = useSession()
  const queryClient = useQueryClient()
  const [message, setMessage] = useState<{ tone: 'good' | 'error'; text: string } | null>(null)
  const [pendingPipeline, setPendingPipeline] = useState<string | null>(null)

  const canTrigger = me?.role === 'admin'

  const health = useQuery({
    queryKey: ['pipeline-health'],
    queryFn: api.pipelineHealth,
    // Faster while something is queued, so Queued → Running → done is visible
    // rather than needing a refresh.
    refetchInterval: (query) =>
      query.state.data?.some((row) => row.trigger_state) ? 10_000 : 60_000,
  })
  const coverage = useQuery({ queryKey: ['coverage'], queryFn: api.coverage })
  const runs = useQuery({
    queryKey: ['pipeline-runs'],
    queryFn: () => api.pipelineRuns({ limit: 200 }),
    refetchInterval: 60_000,
  })

  const trigger = useMutation({
    mutationFn: ({ pipeline, day: target }: { pipeline: string; day?: string }) =>
      api.triggerPipeline(pipeline, target),
    onMutate: ({ pipeline }) => setPendingPipeline(pipeline),
    onSuccess: (accepted) => {
      setMessage({
        tone: 'good',
        text: `${accepted.detail}. This Starts in Under a Minute — The Figures Update When It Finishes.`,
      })
      void queryClient.invalidateQueries({ queryKey: ['pipeline-health'] })
      void queryClient.invalidateQueries({ queryKey: ['coverage'] })
    },
    onError: (error) => {
      setMessage({
        tone: 'error',
        text:
          error instanceof ApiError
            ? error.message
            : 'Could Not Start That.',
      })
    },
    onSettled: () => setPendingPipeline(null),
  })

  if (health.isLoading || coverage.isLoading) return <Skeleton rows={8} />
  if (health.error || coverage.error || !coverage.data) {
    return <Note tone="error">Could Not Load Pipeline Health.</Note>
  }

  const cards = health.data ?? []
  const data = coverage.data
  const abandoned = cards.filter((row) => row.state === 'abandoned')

  return (
    <div>
      <ViewHeader
        title="Connections"
        scoped={false}
      />


      {message && <Note tone={message.tone}>{message.text}</Note>}

      {abandoned.length > 0 && (
        <Note tone="warning">
          {abandoned.length === 1
            ? `The Last ${SOURCE_NAMES[abandoned[0]!.pipeline]} Run Never Finished`
            : `${abandoned.length} Pipelines Have a Run That Never Finished`}
          . A Run Still Marked <code>running</code> After Several Hours Is Reported as
          Abandoned Rather than in Progress: The Process Is Gone, but Nothing Wrote That
          Down.
        </Note>
      )}

      <div className="mb-4">
        <CoverageLegend />
      </div>

      <div className="flex flex-col gap-4">
        {cards.map((row) => (
          <SourceCard
            key={row.pipeline}
            health={row}
            coverage={data}
            runs={runs.data ?? []}
            canTrigger={canTrigger}
            pendingPipeline={pendingPipeline}
            onRun={(pipeline) => trigger.mutate({ pipeline })}
            onRefetchDay={(pipeline, target) => trigger.mutate({ pipeline, day: target })}
          />
        ))}
      </div>

      <p className="mt-4 mb-0 flex flex-wrap gap-4 text-sm">
        <Link to="/connections/matches" style={{ color: 'var(--brand-ink)' }}>
          Episode Matches
        </Link>
      </p>

      {!canTrigger && (
        <p className="mt-3 mb-0 text-xs" style={{ color: 'var(--text-label)' }}>
          Running a Pipeline by Hand Is an Administrator Action.
        </p>
      )}
    </div>
  )
}
